/**
 * LogRowMenu — "…" button at the start of each Notification Logs row.
 * Opens a menu (Roomgrid row-menu pattern); "View data" shows the
 * technical fields that don't earn a column: message id, from, webhook
 * url and webhook status.
 */

import React, { useState } from "react";
import { useRecordContext, useTranslate } from "react-admin";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  MoreHoriz as MoreIcon,
} from "@mui/icons-material";
import { StatusChip } from "@/components/common";
import type { NotificationLogRecord } from "../types/log";

const i18nBase = "resources.notification-logs";

const DetailRow: React.FC<{
  label: string;
  value?: React.ReactNode;
}> = ({ label, value }) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  return (
    <Box sx={{ px: "20px", py: "8px" }}>
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: rg.text3,
          mb: "2px",
        }}
      >
        {label}
      </Typography>
      {typeof value === "string" || value == null ? (
        <Typography
          sx={{
            fontSize: "12.5px",
            fontFamily: rg.fontMono,
            color: rg.text,
            wordBreak: "break-all",
          }}
        >
          {value || "—"}
        </Typography>
      ) : (
        value
      )}
    </Box>
  );
};

export const LogRowMenu: React.FC = () => {
  const record = useRecordContext<NotificationLogRecord>();
  const translate = useTranslate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!record) return null;

  const t = (key: string, fallback: string) =>
    translate(`${i18nBase}.${key}`, { _: fallback });

  return (
    <>
      <IconButton
        size="small"
        aria-label={t("details.title", "View data")}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setDetailsOpen(true);
          }}
        >
          <ListItemIcon>
            <InfoIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText>{t("details.viewData", "View data")}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        onClick={(e) => e.stopPropagation()}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.02em" }}>
            {t("details.title", "Log details")}
          </Typography>
          <IconButton
            onClick={() => setDetailsOpen(false)}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, py: "8px" }}>
          <DetailRow
            label={t("fields.messageId", "Message ID")}
            value={record.messageId}
          />
          <DetailRow label={t("fields.from", "From")} value={record.from} />
          <DetailRow
            label={t("fields.webhookUrl", "Webhook URL")}
            value={record.webhookUrl}
          />
          <DetailRow
            label={t("fields.webhookStatus", "Webhook status")}
            value={
              record.webhookStatus ? <StatusChip source="webhookStatus" /> : "—"
            }
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LogRowMenu;
